import React, { useEffect, useState, useCallback } from 'react';
import api from '../../services/api';

const STATUS_COLORS = {
  completed:       { bg: '#eaf4ef', color: '#2d6a4f' },
  in_delivery:     { bg: '#fef3e2', color: '#b45309' },
  queued_offline:  { bg: '#fef3e2', color: '#b45309' },
  pending_payment: { bg: '#eaf1fb', color: '#1a4e8c' },
  failed:          { bg: '#faeef4', color: '#9b2c5e' },
  refunded:        { bg: '#f0ebe2', color: '#7a7060' },
};

const ALL_STATUSES = ['pending_payment', 'payment_received', 'in_delivery', 'queued_offline', 'completed', 'failed', 'refunded'];

function exportCSV(txs) {
  const headers = ['Ref', 'Receptor', 'Teléfono', 'Monto USD', 'Fee USD', 'Neto USD', 'Método entrega', 'Método pago', 'Estado', 'Agente', 'Fecha', 'Completada'];
  const rows = txs.map(tx => [
    tx.ref, tx.recipient_name, tx.recipient_phone,
    tx.amount_usd, tx.fee_usd, tx.net_usd,
    tx.delivery_method, tx.payment_method, tx.status,
    tx.agent_id || '',
    tx.created_at ? new Date(tx.created_at).toLocaleDateString('es-ES') : '',
    tx.completed_at ? new Date(tx.completed_at).toLocaleDateString('es-ES') : '',
  ]);
  const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `guarapo_ordenes_${new Date().toISOString().split('T')[0]}.csv`;
  a.click(); URL.revokeObjectURL(url);
}

export default function AdminTransactions() {
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [newStatus, setNewStatus] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await api.get('/api/v1/transactions/?limit=500');
      setTxs(Array.isArray(res.data) ? res.data : []);
    } catch { setTxs([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = txs.filter(tx => {
    const matchFilter = filter === 'all' || tx.status === filter;
    const matchSearch = !search ||
      tx.ref?.toLowerCase().includes(search.toLowerCase()) ||
      tx.recipient_name?.toLowerCase().includes(search.toLowerCase()) ||
      tx.recipient_phone?.includes(search);
    return matchFilter && matchSearch;
  });

  const totalVol = filtered.filter(t => t.status === 'completed').reduce((s, t) => s + (t.amount_usd || 0), 0);
  const totalFee = filtered.filter(t => t.status === 'completed').reduce((s, t) => s + (t.fee_usd || 0), 0);

  const handleStatusUpdate = async () => {
    if (!selected || !newStatus) return;
    setUpdatingStatus(true);
    try {
      await api.patch(`/api/v1/transactions/${selected.ref}/status`, { status: newStatus });
      setTxs(ts => ts.map(t => t.ref === selected.ref ? { ...t, status: newStatus } : t));
      setSelected(prev => ({ ...prev, status: newStatus }));
      setNewStatus('');
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al actualizar estado');
    } finally { setUpdatingStatus(false); }
  };

  return (
    <div style={{ display: selected ? 'grid' : 'block', gridTemplateColumns: '1fr 400px', gap: 18 }}>
      <div>
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h1 style={{ fontFamily: 'Georgia,serif', fontSize: 26, margin: '0 0 4px' }}>Órdenes</h1>
              <p style={{ fontSize: 13, color: '#7a7060', margin: 0 }}>
                {filtered.length} órdenes · Vol: ${totalVol.toFixed(0)} · Fees: ${totalFee.toFixed(2)}
              </p>
            </div>
            <button onClick={() => exportCSV(filtered)} style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid #e2dbd0', background: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
              ⬇ Exportar CSV
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <input style={{ padding: '9px 14px', border: '1px solid #e2dbd0', borderRadius: 8, fontSize: 13, outline: 'none', width: 220 }}
            placeholder="Buscar ref, receptor..."
            value={search} onChange={e => setSearch(e.target.value)} />
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {['all', ...ALL_STATUSES].map(f => (
              <button key={f} onClick={() => setFilter(f)} style={{ padding: '7px 12px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600, background: filter === f ? '#c94a1e' : '#f0ebe2', color: filter === f ? '#fff' : '#7a7060' }}>
                {f === 'all' ? 'Todas' : f.replace('_', ' ')}
              </button>
            ))}
          </div>
        </div>

        <div style={{ background: '#fff', border: '1px solid #e2dbd0', borderRadius: 14, overflow: 'hidden', overflowX: 'auto' }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#7a7060' }}>Cargando...</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#7a7060' }}>No hay órdenes</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
              <thead>
                <tr>{['Ref', 'Receptor', 'Monto', 'Entrega', 'Estado', 'Fecha'].map(h => (
                  <th key={h} style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#7a7060', padding: '10px 14px', background: '#faf8f5', borderBottom: '1px solid #e2dbd0', textAlign: 'left', whiteSpace: 'nowrap' }}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {filtered.map((tx, i) => {
                  const sc = STATUS_COLORS[tx.status] || { bg: '#f0ebe2', color: '#7a7060' };
                  return (
                    <tr key={i} onClick={() => { setSelected(selected?.ref === tx.ref ? null : tx); setNewStatus(''); }}
                      style={{ cursor: 'pointer', background: selected?.ref === tx.ref ? '#fdf4f0' : '' }}
                      onMouseOver={e => { if (selected?.ref !== tx.ref) e.currentTarget.style.background = '#f9f6f1'; }}
                      onMouseOut={e => { if (selected?.ref !== tx.ref) e.currentTarget.style.background = ''; }}>
                      <td style={{ padding: '12px 14px', borderBottom: '1px solid #f0ebe2', fontFamily: 'monospace', fontSize: 11, color: '#c94a1e' }}>{tx.ref}</td>
                      <td style={{ padding: '12px 14px', borderBottom: '1px solid #f0ebe2' }}>
                        <b style={{ fontSize: 13 }}>{tx.recipient_name}</b>
                        <div style={{ fontSize: 11, color: '#7a7060' }}>{tx.recipient_phone}</div>
                      </td>
                      <td style={{ padding: '12px 14px', borderBottom: '1px solid #f0ebe2' }}>
                        <b>${tx.amount_usd}</b>
                        <div style={{ fontSize: 11, color: '#7a7060' }}>fee ${tx.fee_usd}</div>
                      </td>
                      <td style={{ padding: '12px 14px', borderBottom: '1px solid #f0ebe2', fontSize: 12, color: '#7a7060' }}>{tx.delivery_method?.replace('_', ' ')}</td>
                      <td style={{ padding: '12px 14px', borderBottom: '1px solid #f0ebe2' }}>
                        <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: sc.bg, color: sc.color }}>
                          {tx.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td style={{ padding: '12px 14px', borderBottom: '1px solid #f0ebe2', fontSize: 11, color: '#7a7060', whiteSpace: 'nowrap' }}>
                        {tx.created_at ? new Date(tx.created_at).toLocaleDateString('es-ES') : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Detail panel */}
      {selected && (
        <div style={{ background: '#fff', border: '1px solid #e2dbd0', borderRadius: 14, padding: 22, height: 'fit-content', position: 'sticky', top: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 18 }}>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: 17 }}>Detalle orden</div>
            <button onClick={() => setSelected(null)} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 18, color: '#7a7060' }}>✕</button>
          </div>

          <div style={{ fontFamily: 'monospace', fontSize: 13, color: '#c94a1e', marginBottom: 16, fontWeight: 700 }}>{selected.ref}</div>

          {[
            ['Receptor', selected.recipient_name],
            ['Teléfono', selected.recipient_phone],
            ['Monto total', `$${selected.amount_usd}`],
            ['Fee Guarapo', `$${selected.fee_usd}`],
            ['Neto al receptor', `$${selected.net_usd}`],
            ['Método entrega', selected.delivery_method],
            ['Método pago', selected.payment_method],
            ['Agente ID', selected.agent_id || '—'],
            ['Creada', selected.created_at ? new Date(selected.created_at).toLocaleString('es-ES') : '—'],
            ['Completada', selected.completed_at ? new Date(selected.completed_at).toLocaleString('es-ES') : '—'],
          ].map(([k, v]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0ebe2', fontSize: 13 }}>
              <span style={{ color: '#7a7060' }}>{k}</span>
              <span style={{ fontWeight: 600, textAlign: 'right', maxWidth: 200, wordBreak: 'break-all' }}>{v}</span>
            </div>
          ))}

          {/* Status badge */}
          <div style={{ marginTop: 12, marginBottom: 16, textAlign: 'center' }}>
            <span style={{ padding: '6px 16px', borderRadius: 20, fontSize: 12, fontWeight: 700, background: STATUS_COLORS[selected.status]?.bg || '#f0ebe2', color: STATUS_COLORS[selected.status]?.color || '#7a7060' }}>
              Estado actual: {selected.status.replace('_', ' ')}
            </span>
          </div>

          {selected.failure_reason && (
            <div style={{ background: '#faeef4', borderRadius: 8, padding: '10px 12px', fontSize: 12, color: '#9b2c5e', marginBottom: 14 }}>
              <b>Motivo de fallo:</b> {selected.failure_reason}
            </div>
          )}

          {/* Change status */}
          <div style={{ borderTop: '1px solid #e2dbd0', paddingTop: 16, marginTop: 8 }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#7a7060', marginBottom: 8 }}>
              Cambiar estado manualmente
            </div>
            <select
              value={newStatus}
              onChange={e => setNewStatus(e.target.value)}
              style={{ width: '100%', padding: '9px 12px', border: '1px solid #e2dbd0', borderRadius: 8, fontSize: 13, outline: 'none', marginBottom: 8, cursor: 'pointer' }}>
              <option value="">Seleccionar nuevo estado...</option>
              {ALL_STATUSES.filter(s => s !== selected.status).map(s => (
                <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
              ))}
            </select>
            <button onClick={handleStatusUpdate} disabled={!newStatus || updatingStatus} style={{ width: '100%', padding: '10px', borderRadius: 8, border: 'none', background: (!newStatus || updatingStatus) ? '#e2dbd0' : '#c94a1e', color: '#fff', fontSize: 13, fontWeight: 600, cursor: (!newStatus || updatingStatus) ? 'not-allowed' : 'pointer' }}>
              {updatingStatus ? 'Actualizando...' : 'Actualizar estado'}
            </button>
            <div style={{ fontSize: 11, color: '#b0a090', marginTop: 6, textAlign: 'center' }}>
              ⚠ Los cambios manuales se registran en el audit trail
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
